<template>
  <div class="reporting-section">
    <div class="section-header">
      <i class="material-icons section-icon">compare_arrows</i>
      <h2>{{ $t('dash.reporting.section.comparison.title') }}</h2>
      <span class="page-count">{{ pagesData.length + pendingPages.length }} pages</span>
      <Tooltip :text="pdfTooltip" :active="!!(prevLoading || concurrentsLoading)" position="bottom">
        <button class="btn-pdf" :disabled="pdfLoading || prevLoading || concurrentsLoading" @click="downloadComparisonPdf">
          <i class="material-icons">{{ pdfLoading ? 'hourglass_empty' : 'picture_as_pdf' }}</i>
          {{ pdfLoading ? '…' : 'PDF' }}
        </button>
      </Tooltip>
    </div>

    <!-- ── Layout normal : métriques en lignes, pages en colonnes (≤ 7 pages) ── -->
    <div class="comparison-table" v-if="!isInverted">
      <div class="cmp-row cmp-head">
        <div class="cmp-metric-label"></div>
        <div class="cmp-page-col" v-for="item in pagesData" :key="item.page.id">
          <div class="page-badge page-badge--clickable" @click="$emit('drill-down', item.page.id)" :title="$t('dash.reporting.see_detail')">
            <img v-if="item.page.picture_url || item.page.avatar" :src="item.page.picture_url || item.page.avatar" class="page-avatar" alt="">
            <span class="page-name">{{ item.page.name }}</span>
            <span class="network-badge">{{ item.page.network }}</span>
            <span class="drill-hint"><i class="material-icons">open_in_new</i></span>
          </div>
        </div>
        <div class="cmp-page-col cmp-page-col--pending" v-for="p in pendingPages" :key="'pending-' + p.page.id" :title="$t('dash.reporting.page_loading')">
          <div class="page-badge">
            <img v-if="p.page.picture_url || p.page.avatar" :src="p.page.picture_url || p.page.avatar" class="page-avatar page-avatar--pending" alt="">
            <span class="page-name">{{ p.page.name }}</span>
            <span class="network-badge">{{ p.page.network }}</span>
            <div class="cmp-progress"><div class="cmp-progress-bar" :style="{ width: p.progress + '%' }"></div></div>
            <span class="cmp-progress-pct">{{ p.progress }}%</span>
          </div>
        </div>
        <div class="cmp-page-col cmp-total-col">
          <div class="page-badge"><span class="page-name">Total</span></div>
        </div>
      </div>
      <div class="cmp-row" v-for="metric in metrics" :key="metric.key">
        <div class="cmp-metric-label">{{ metric.label }}</div>
        <div class="cmp-page-col" v-for="item in pagesData" :key="item.page.id"
             :class="{ 'is-best': getBest(metric.key) === item.page.id }">
          <span class="cmp-value">{{ formatValue(getValue(item, metric.key), metric.format) }}</span>
          <span v-if="prevLoading && !item.stats.page.previous_period" class="cmp-evol cmp-evol--loading">…</span>
          <span v-else-if="getEvolution(item, metric.key) !== null" class="cmp-evol" :class="evolClass(item, metric.key)">
            <i class="material-icons">{{ getEvolution(item, metric.key) >= 0 ? 'arrow_upward' : 'arrow_downward' }}</i>
            {{ formatEvol(getEvolution(item, metric.key)) }}
          </span>
        </div>
        <div class="cmp-page-col cmp-page-col--pending" v-for="p in pendingPages" :key="'pending-' + p.page.id">
          <span class="cmp-value cmp-value--pending">—</span>
        </div>
        <div class="cmp-page-col cmp-total-col">
          <span class="cmp-value cmp-total-value">{{ formatValue(getTotal(metric.key), metric.format) }}</span>
          <span v-if="prevLoading && !hasSomePrevPeriod" class="cmp-evol cmp-evol--loading">…</span>
          <span v-else-if="getTotalEvolution(metric.key) !== null" class="cmp-evol" :class="totalEvolClass(metric.key)">
            <i class="material-icons">{{ getTotalEvolution(metric.key) >= 0 ? 'arrow_upward' : 'arrow_downward' }}</i>
            {{ formatEvol(getTotalEvolution(metric.key)) }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Layout inversé : pages en lignes, métriques en colonnes (> 7 pages) ── -->
    <div class="comparison-table comparison-table--inverted" v-else>
      <div class="cmp-inv-row cmp-inv-head">
        <div class="cmp-inv-page-col"></div>
        <div class="cmp-inv-metric-col" v-for="metric in metrics" :key="metric.key">
          {{ metric.shortLabel }}
        </div>
      </div>
      <div class="cmp-inv-row" v-for="item in pagesData" :key="item.page.id">
        <div class="cmp-inv-page-col">
          <div class="inv-page-badge inv-page-badge--clickable" @click="$emit('drill-down', item.page.id)" :title="$t('dash.reporting.see_detail')">
            <img v-if="item.page.picture_url || item.page.avatar" :src="item.page.picture_url || item.page.avatar" class="inv-page-avatar" alt="">
            <div class="inv-page-info">
              <span class="inv-page-name">{{ item.page.name }}</span>
              <span class="network-badge">{{ item.page.network }}</span>
            </div>
          </div>
        </div>
        <div class="cmp-inv-metric-col" v-for="metric in metrics" :key="metric.key"
             :class="{ 'is-best': getBest(metric.key) === item.page.id }">
          <span class="cmp-value">{{ formatValue(getValue(item, metric.key), metric.format) }}</span>
          <span v-if="prevLoading && !item.stats.page.previous_period" class="cmp-evol cmp-evol--loading">…</span>
          <span v-else-if="getEvolution(item, metric.key) !== null" class="cmp-evol" :class="evolClass(item, metric.key)">
            <i class="material-icons">{{ getEvolution(item, metric.key) >= 0 ? 'arrow_upward' : 'arrow_downward' }}</i>
            {{ formatEvol(getEvolution(item, metric.key)) }}
          </span>
        </div>
      </div>

      <!-- Lignes des pages en cours de chargement (Lambda / RequestManager) -->
      <div class="cmp-inv-row cmp-inv-row--pending" v-for="p in pendingPages" :key="'pending-' + p.page.id" :title="$t('dash.reporting.page_loading')">
        <div class="cmp-inv-page-col">
          <div class="inv-page-badge">
            <img v-if="p.page.picture_url || p.page.avatar" :src="p.page.picture_url || p.page.avatar" class="inv-page-avatar inv-page-avatar--pending" alt="">
            <div class="inv-page-info">
              <span class="inv-page-name">{{ p.page.name }}</span>
              <span class="network-badge">{{ p.page.network }}</span>
            </div>
          </div>
        </div>
        <div class="cmp-inv-pending-cell">
          <div class="cmp-progress"><div class="cmp-progress-bar" :style="{ width: p.progress + '%' }"></div></div>
          <span class="cmp-progress-pct">{{ p.progress }}%</span>
        </div>
      </div>

      <!-- Ligne Total -->
      <div class="cmp-inv-row cmp-inv-total-row">
        <div class="cmp-inv-page-col">
          <div class="inv-page-badge">
            <div class="inv-page-info">
              <span class="inv-page-name inv-total-label">Total</span>
            </div>
          </div>
        </div>
        <div class="cmp-inv-metric-col" v-for="metric in metrics" :key="metric.key">
          <span class="cmp-value cmp-total-value">{{ formatValue(getTotal(metric.key), metric.format) }}</span>
          <span v-if="prevLoading && !hasSomePrevPeriod" class="cmp-evol cmp-evol--loading">…</span>
          <span v-else-if="getTotalEvolution(metric.key) !== null" class="cmp-evol" :class="totalEvolClass(metric.key)">
            <i class="material-icons">{{ getTotalEvolution(metric.key) >= 0 ? 'arrow_upward' : 'arrow_downward' }}</i>
            {{ formatEvol(getTotalEvolution(metric.key)) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Tooltip from '../tooltip/tooltipInit.vue'
import { EventBus } from '@/event-bus'

export default {
  name: 'ReportingComparison',
  components: { Tooltip },
  props: {
    pagesData: { type: Array, required: true },
    dateStart: { default: null },
    dateEnd: { default: null },
    sortCriterion: { type: String, default: 'engagement' },
    concurrentData: { type: Object, default: () => ({}) },
    prevLoading: { type: Boolean, default: false },
    concurrentsLoading: { type: Boolean, default: false },
    pendingPages: { type: Array, default: () => [] },
  },
  data() {
    return { pdfLoading: false }
  },
  computed: {
    hasSomePrevPeriod() {
      return this.pagesData.some(item => item.stats && item.stats.page && item.stats.page.previous_period)
    },
    pdfTooltip() {
      if (this.prevLoading) return this.$t('dash.reporting.section.comparison.pdf_wait_prev')
      if (this.concurrentsLoading) return this.$t('dash.reporting.section.comparison.pdf_wait_concurrent')
      return ''
    },
    isInverted() {
      return (this.pagesData.length + this.pendingPages.length) > 7
    },
    metrics() {
      return [
        { key: 'page_fans',       label: this.$t('dash.reporting.followers'),       shortLabel: 'Abon.',   format: 'number' },
        { key: 'nb_posts',        label: this.$t('dash.reporting.publications'),    shortLabel: 'Posts',   format: 'number' },
        { key: 'totalImpressions',label: this.$t('dash.reporting.impression'),      shortLabel: 'Impr.',   format: 'number' },
        { key: 'totalEngagement', label: this.$t('dash.reporting.interactions'),    shortLabel: 'Inter.',  format: 'number' },
        { key: 'totalReactions',  label: this.$t('dash.reporting.reaction'),        shortLabel: 'React.',  format: 'number' },
        { key: 'totalComments',   label: this.$t('dash.reporting.comment'),         shortLabel: 'Comm.',   format: 'number' },
        { key: 'totalShares',     label: this.$t('dash.reporting.share'),           shortLabel: 'Part.',   format: 'number' },
        { key: 'engagement_rate', label: this.$t('dash.reporting.engagement_rate'), shortLabel: 'Eng.%',   format: 'percent' },
      ]
    },
  },
  methods: {
    effectivePageValue(item, key) {
      var p = item.stats && item.stats.page
      if (!p) return 0
      if (key === 'totalReactions') {
        if (p.totalReactions) return p.totalReactions
        return ((item.stats.posts) || []).reduce(function(s, post) {
          return s + (post.post_reactions_sum || post.reactions_count || post.like_count || 0)
        }, 0)
      }
      if (key === 'totalEngagement') {
        if (p.totalEngagement) return p.totalEngagement
        return ((item.stats.posts) || []).reduce(function(s, post) {
          return s + (post.post_reactions_sum || post.reactions_count || post.like_count || 0)
                 + (post.comments_count || 0)
                 + (post.shares_count || 0)
        }, 0)
      }
      return p[key] || 0
    },
    getValue(item, key) {
      if (!item.stats || !item.stats.page) return 0
      if (key === 'engagement_rate') {
        // Même précédence que ReportingOverview.engagementRate (taux propre au réseau d'abord)
        var reach = parseFloat(item.stats.page.totalReachEngag)
        if (Number.isFinite(reach)) return reach
        var eng = this.effectivePageValue(item, 'totalEngagement')
        var imp = item.stats.page.totalImpressions || 0
        return imp > 0 ? parseFloat((eng / imp * 100).toFixed(2)) : 0
      }
      return this.effectivePageValue(item, key)
    },
    getPrevValue(item, key) {
      if (!item.stats || !item.stats.page || !item.stats.page.previous_period) return null
      var prev = item.stats.page.previous_period
      if (key === 'engagement_rate') {
        var reach = parseFloat(prev.totalReachEngag)
        if (Number.isFinite(reach)) return reach
      }
      return prev[key] != null ? prev[key] : null
    },
    getEvolution(item, key) {
      var cur = this.getValue(item, key)
      var prev = this.getPrevValue(item, key)
      if (prev === null) return null
      if (prev === 0) return cur > 0 ? 100 : 0
      return parseFloat(((cur - prev) / Math.abs(prev) * 100).toFixed(1))
    },
    evolClass(item, key) {
      var e = this.getEvolution(item, key)
      if (e === null || e === 0) return 'cmp-evol--neutral'
      return e > 0 ? 'cmp-evol--up' : 'cmp-evol--down'
    },
    getTotalPrev(key) {
      var hasPrev = this.pagesData.some(item => this.getPrevValue(item, key) !== null)
      if (!hasPrev) return null
      // Une page sans abonnés comparables (période précédente chargée) fausserait le total
      if (key === 'page_fans' && this.pagesData.some(item => item.stats && item.stats.page && item.stats.page.previous_period && this.getPrevValue(item, key) === null)) return null
      if (key === 'engagement_rate') {
        var totalEng = this.pagesData.reduce((s, item) => s + (this.getPrevValue(item, 'totalEngagement') || 0), 0)
        var totalImp = this.pagesData.reduce((s, item) => s + (this.getPrevValue(item, 'totalImpressions') || 0), 0)
        return totalImp > 0 ? parseFloat((totalEng / totalImp * 100).toFixed(2)) : 0
      }
      return this.pagesData.reduce((s, item) => s + (this.getPrevValue(item, key) || 0), 0)
    },
    getTotalEvolution(key) {
      var cur = this.getTotal(key)
      var prev = this.getTotalPrev(key)
      if (prev === null) return null
      if (prev === 0) return cur > 0 ? 100 : 0
      return parseFloat(((cur - prev) / Math.abs(prev) * 100).toFixed(1))
    },
    totalEvolClass(key) {
      var e = this.getTotalEvolution(key)
      if (e === null || e === 0) return 'cmp-evol--neutral'
      return e > 0 ? 'cmp-evol--up' : 'cmp-evol--down'
    },
    formatEvol(e) {
      if (e === null) return ''
      return (e > 0 ? '+' : '') + e + '%'
    },
    formatValue(val, format) {
      if (format === 'percent') return (val || 0).toFixed(2) + '%'
      return (val || 0).toLocaleString()
    },
    getBest(key) {
      if (!this.pagesData.length) return null
      let best = this.pagesData[0]
      this.pagesData.forEach(item => {
        if (this.getValue(item, key) > this.getValue(best, key)) best = item
      })
      return best.page.id
    },
    getTotal(key) {
      if (key === 'engagement_rate') {
        var totalEng = this.pagesData.reduce((s, item) => s + (this.getValue(item, 'totalEngagement')), 0)
        var totalImp = this.pagesData.reduce((s, item) => s + (this.getValue(item, 'totalImpressions')), 0)
        return totalImp > 0 ? parseFloat((totalEng / totalImp * 100).toFixed(2)) : 0
      }
      return this.pagesData.reduce((s, item) => s + this.getValue(item, key), 0)
    },
    async downloadComparisonPdf() {
      if (this.pdfLoading) return
      const { VUE_APP_API_HOST } = process.env
      const token = localStorage.getItem('token')
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'fr'
      const period = this.dateStart ? String(this.dateStart) : 'period'
      const filename = `reporting-comparison-${period}.pdf`

      this.pdfLoading = true
      try {
        // 1. Démarrer la génération → obtenir un sessionId
        const startResp = await fetch(`${VUE_APP_API_HOST}/reporting/pdf/v2/comparison/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            pagesData: this.pagesData,
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            locale,
            sortCriterion: this.sortCriterion,
            concurrentsMap: this.concurrentData && Object.keys(this.concurrentData).length ? this.concurrentData : null,
          }),
        })
        if (!startResp.ok) throw new Error('PDF start failed: ' + startResp.status)
        const { sessionId } = await startResp.json()

        this.pdfLoading = false  // Libère le bouton immédiatement

        // 2. RequestManager avec progression SSE
        const pageCount = this.pagesData.length
        EventBus.$emit('requestManagerAdd', (onSuccess, onProgress, onError) => {
          return new Promise((resolve, reject) => {
            const es = new EventSource(`${VUE_APP_API_HOST}/reporting/pdf/v2/stream?sessionId=${sessionId}`)
            es.onerror = () => { es.close(); onError(); reject() }
            es.onmessage = (event) => {
              const data = JSON.parse(event.data)
              if (data.error) { es.close(); onError(data.message); reject(); return }
              if (data.pct != null && !data.done) {
                onProgress(data.pct + '% · ' + (data.message || ''))
                return
              }
              if (data.done) {
                es.close()
                const a = document.createElement('a')
                a.href = `${VUE_APP_API_HOST}/reporting/pdf/v2/download?sessionId=${sessionId}`
                a.download = filename
                document.body.appendChild(a)
                a.click()
                setTimeout(() => document.body.removeChild(a), 1000)
                onSuccess('PDF téléchargé !')
                resolve()
              }
            }
          })
        }, {
          title: pageCount + ' pages — PDF comparatif',
          picture: null,
          loadingText: 'Initialisation...',
          successText: 'PDF téléchargé',
          autoOpen: true,
        })
      } catch (e) {
        console.error('[comparison pdf]', e)
        this.pdfLoading = false
      }
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }
.page-count {
  margin-left: auto;
  font-size: 12px;
  color: #9ba3b7;
  background: #f1edf5;
  padding: 2px 10px;
  border-radius: 20px;
}
.btn-pdf {
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent !important;
  color: #6B1F4D !important;
  border: 1.5px solid #6B1F4D !important;
  border-radius: 8px;
  padding: 4px 11px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-pdf:hover:not(:disabled) { background: #6B1F4D; color: #fff; }
.btn-pdf:disabled { opacity: 0.45; cursor: default; }
.btn-pdf .material-icons { font-size: 14px; }

.comparison-table { overflow-x: auto; }
.cmp-row {
  display: flex;
  border-bottom: 1px solid #f9f7fb;
  min-width: 0;
}
.cmp-row:last-child { border-bottom: none; }
.cmp-head { border-bottom: 2px solid #f1edf5; padding-bottom: 10px; }

.cmp-metric-label {
  width: 160px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 10px 8px 10px 0;
  display: flex;
  align-items: center;
}
.cmp-page-col {
  flex: 1;
  min-width: 120px;
  padding: 10px 8px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.cmp-page-col.is-best .cmp-value {
  color: #6B1F4D;
  font-weight: 700;
}

.page-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.page-badge--clickable {
  cursor: pointer;
  border-radius: 5.6px;
  padding: 4px;
  transition: background 0.15s;
}
.page-badge--clickable:hover { background: #f1edf5; }
.page-badge--clickable:hover .drill-hint { opacity: 1; }
.drill-hint {
  opacity: 0;
  transition: opacity 0.15s;
  display: flex;
  align-items: center;
}
.drill-hint .material-icons { font-size: 13px; color: #6B1F4D; }
.page-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f1edf5;
}
.page-name {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-align: center;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.network-badge {
  font-size: 10px;
  color: #9ba3b7;
  background: #f1edf5;
  padding: 1px 6px;
  border-radius: 10px;
  text-transform: capitalize;
}
.cmp-value {
  font-size: 14px;
  font-weight: 500;
  color: #121826;
}
.cmp-evol {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 5px;
}
.cmp-evol .material-icons { font-size: 10px; }
.cmp-evol--up      { color: #16a34a; background: #f0fdf4; }
.cmp-evol--down    { color: #dc2626; background: #fef2f2; }
.cmp-evol--neutral { color: #9ba3b7; background: #f1edf5; }
.cmp-evol--loading { color: #c4b5d0; background: none; font-size: 11px; }
.cmp-total-col {
  background: #faf8fc;
  border-left: 2px solid #f1edf5;
}
.cmp-head .cmp-total-col {
  display: flex;
  align-items: center;
  justify-content: center;
}
.cmp-total-value {
  font-weight: 700;
  color: #6B1F4D;
}

/* ── Layout inversé (> 7 pages) ─────────────────────────────────────────── */
.comparison-table--inverted { overflow-x: auto; }

.cmp-inv-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f9f7fb;
  min-width: 0;
}
.cmp-inv-row:last-child { border-bottom: none; }
.cmp-inv-head {
  border-bottom: 2px solid #f1edf5;
  padding-bottom: 6px;
}

.cmp-inv-page-col {
  width: 170px;
  flex-shrink: 0;
  padding: 8px 8px 8px 0;
}
.cmp-inv-metric-col {
  flex: 1;
  min-width: 72px;
  padding: 6px 4px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}
.cmp-inv-head .cmp-inv-metric-col {
  font-size: 11px;
  font-weight: 700;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.cmp-inv-metric-col.is-best .cmp-value { color: #6B1F4D; font-weight: 700; }

.inv-page-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px 4px 0;
}
.inv-page-badge--clickable { cursor: pointer; border-radius: 5.6px; transition: background 0.15s; }
.inv-page-badge--clickable:hover { background: #f1edf5; }

.inv-page-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid #f1edf5;
  flex-shrink: 0;
}
.inv-page-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.cmp-inv-total-row {
  background: #faf8fc;
  border-top: 2px solid #f1edf5 !important;
  border-bottom: none !important;
}
.inv-total-label {
  font-weight: 700;
  color: #6B1F4D;
}

.inv-page-name {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

/* ── Pages en cours de chargement (Lambda / RequestManager) ─────────────── */
.cmp-progress {
  width: 100%;
  max-width: 90px;
  height: 5px;
  border-radius: 3px;
  background: #f1edf5;
  overflow: hidden;
}
.cmp-progress-bar {
  height: 100%;
  border-radius: 3px;
  background: #6B1F4D;
  transition: width 0.4s ease;
}
.cmp-progress-pct {
  font-size: 10px;
  font-weight: 600;
  color: #9ba3b7;
}
.page-avatar--pending, .inv-page-avatar--pending { opacity: 0.5; filter: grayscale(0.4); }
.cmp-value--pending { color: #d7c9e0; }
.cmp-inv-pending-cell {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
}
</style>
